import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import contractService from '../../services/contractService';
import countryService from '../../services/countryService';
import { useCountry } from '../../hooks/useCountry';
import PDFViewer from './PDFViewer';
import PaymentModal from './PaymentModal';
import { Button, Alert, Spinner, Badge } from '../ui';

const ContractPreview = () => {
  const { contractId } = useParams();
  const { countryCode, theme, country: countryConfig } = useCountry();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const colors = theme?.colors || {};
  const primaryColor = colors.primary || '#2563eb';
  const jurisdiction = countryCode || 'usa';
  const [countryData, setCountryData] = useState(countryService.getCountryFromCache(jurisdiction));
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [countryConfig, i18n]);

  useEffect(() => {
    const fetchCountryData = async () => {
      const data = await countryService.getCountryByCode(jurisdiction);
      if (data) setCountryData(data);
    };
    fetchCountryData();
  }, [jurisdiction]);

  useEffect(() => {
    loadContract();
  }, [contractId]);

  const getCurrencySymbol = (currency) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', CAD: 'CA$', AUD: 'A$' };
    return symbols[currency] || '$';
  };

  const getContractTypeLabel = () => t('contracts.types.residential');

  const loadContract = async () => {
    try {
      setLoading(true);
      const data = await contractService.getContract(jurisdiction, contractId);
      setContract(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load contract:', error);
      setError(t('preview.unableToLoad'));
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setError(null);
      setPurchasing(true);
      const price = countryData?.price || 29.99;
      const currency = countryData?.currency || 'USD';
      const amountInCents = Math.round(price * 100);
      const result = await contractService.createPaymentIntent(contractId, amountInCents, currency);
      setClientSecret(result.clientSecret);
      setPaymentIntentId(result.paymentIntentId);
      setShowPaymentModal(true);
      setPurchasing(false);
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      setError(t('preview.paymentError'));
      setPurchasing(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent, email = '') => {
    try {
      await contractService.confirmPayment(paymentIntent.id, email);
      setShowPaymentModal(false);
      navigate(`/contracts/${contractId}/download`);
    } catch (error) {
      console.error('Payment confirmation failed:', error);
      setError(t('preview.confirmationError'));
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error.message || 'Payment failed. Please try again.');
  };

  const previewUrl = contractService.getPreviewUrl(jurisdiction, contractId);
  const currencySymbol = getCurrencySymbol(countryData?.currency);
  const price = countryData?.price || '29.99';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
        <Spinner.Page message={t('preview.loadingPreview')} />
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
        <div className="bg-white rounded-2xl shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('preview.unableToLoad')}</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} fullWidth style={theme ? { background: colors.buttonBg } : undefined}>
            {t('preview.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 lg:pb-0" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                title={t('common.back')}
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">{t('preview.title')}</h1>
                </div>
                <p className="text-sm text-gray-500">{getContractTypeLabel()}</p>
              </div>
            </div>

            {/* SSL + Stripe trust in header */}
            <div className="hidden sm:flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>SSL</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <svg className="w-3.5 h-3.5 text-indigo-500" viewBox="0 0 32 32" fill="currentColor">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697.309 13.134.309c-2.627 0-4.791.733-6.314 2.12C5.297 3.828 4.482 5.5 4.482 7.593c0 3.68 2.276 5.29 6.012 6.636 2.458.89 3.235 1.52 3.235 2.567 0 1.013-.817 1.631-2.386 1.631-1.936 0-4.9-.859-6.89-2.075l-.89 5.579c1.67.91 4.76 1.731 7.846 1.731 2.744 0 5.002-.716 6.593-2.09 1.743-1.499 2.627-3.63 2.627-6.256-.014-3.763-2.317-5.388-6.654-6.957z"/>
                </svg>
                <span>Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* PDF Preview - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Positive outcome banner instead of warning */}
            <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}12, ${colors.primaryLight}18)` : 'linear-gradient(135deg, #eff6ff, #eef2ff)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #2563eb, #4f46e5)' }}>
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{t('preview.readyBannerTitle', 'Votre bail est prêt !')}</p>
                <p className="text-sm text-gray-600 mt-0.5">{t('preview.readyBannerDesc', 'Achetez pour télécharger la version finale sans filigrane en 30 secondes.')}</p>
              </div>
              <div className="hidden sm:flex flex-col items-end text-right">
                <span className="text-xs text-gray-500 mb-1">{t('preview.expiresIn', 'Disponible pendant')}</span>
                <span className="text-sm font-semibold" style={{ color: primaryColor }}>48h</span>
              </div>
            </div>

            {/* PDF Viewer Card */}
            <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{t('preview.documentPreview')}</h2>
                    <p className="text-sm text-gray-500 mt-1">{t('preview.scrollToReview')}</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {t('preview.viewOnly')}
                  </div>
                </div>
              </div>
              <div className="bg-gray-50">
                <PDFViewer pdfUrl={previewUrl} />
              </div>
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-5">

              {/* Purchase Card — primary conversion unit */}
              <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
                <div className="p-6 text-white" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium opacity-90">{t('preview.finalDocument')}</span>
                    {/* Legal credibility badge */}
                    <span className="text-xs px-2 py-1 rounded-full bg-white/20 font-medium">
                      {t('preview.legalBadge', 'Conforme loi Alur')}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold">{currencySymbol}{price}</span>
                  </div>
                  <p className="text-sm opacity-75">{t('preview.oneTimePayment')}</p>
                  {/* Price anchor vs notary */}
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-xs opacity-70">{t('preview.vsNotary', 'Notaire ou avocat : 150 – 400 €. Ici : paiement unique.')}</p>
                  </div>
                </div>

                <div className="p-6">
                  {/* Reordered features — lead with compliance */}
                  <div className="space-y-3 mb-5">
                    {[
                      t('preview.legallyFormatted'),
                      t('preview.readyToPrint'),
                      t('preview.editableWord'),
                      t('preview.lifetimeAccess'),
                      t('preview.instantDownload', 'Téléchargement instantané'),
                    ].map((text, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm">
                        <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                        <span className="text-gray-700">{text}</span>
                      </div>
                    ))}
                  </div>

                  {error && (
                    <Alert type="error" className="mb-4" dismissible onDismiss={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  {/* CTA button — outcome-focused copy with price */}
                  <Button
                    onClick={handlePurchase}
                    loading={purchasing}
                    fullWidth
                    size="lg"
                    style={theme ? { background: colors.buttonBg } : { background: 'linear-gradient(135deg, #1d4ed8, #4338ca)' }}
                    icon={({ className }) => (
                      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    )}
                  >
                    {purchasing ? t('preview.processing') : `${t('preview.getMyContract', 'Obtenir mon bail')} — ${currencySymbol}${price}`}
                  </Button>

                  {/* 30-day guarantee — prominent */}
                  <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-3">
                    <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p className="text-xs text-emerald-700 font-medium leading-snug">{t('preview.moneyBackGuarantee', 'Garantie 30 jours — remboursement intégral si vous n\'êtes pas satisfait.')}</p>
                  </div>

                  {/* Secure payment with Stripe logo */}
                  <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    {t('preview.securePayment')}
                    <svg className="w-8 h-3 text-indigo-500 ml-1" viewBox="0 0 60 25" fill="currentColor">
                      <path d="M26.28 7.498c0-1.228-.96-1.96-2.188-1.96-1.732 0-2.748 1.196-2.748 2.576 0 1.624 1.148 2.56 2.692 2.56.784 0 1.512-.224 2.048-.644V8.57c-.48.308-1.064.476-1.62.476-.7 0-1.26-.252-1.484-.84h3.3v-.708zm-3.3-.28c.168-.7.636-1.12 1.26-1.12.588 0 1.008.392 1.008 1.12h-2.268zm8.176-1.68c-.896 0-1.568.392-1.932.952l-.112-.84h-1.428v6.776h1.54V9.854c0-.98.532-1.568 1.428-1.568.168 0 .364.028.532.056V6.734a2.63 2.63 0 00-.028 0zm-8.736 5.888h1.54V5.538h-1.54v5.888zm0-7.084h1.54V2.934h-1.54v1.408zM9.896 11.426h1.54V2.934h-1.54v8.492zm18.48-3.64c0-1.624-1.204-2.548-2.856-2.548-1.708 0-2.94 1.064-2.94 2.772 0 1.792 1.26 2.828 3.052 2.828.868 0 1.652-.252 2.24-.728v-1.26c-.504.392-1.176.644-1.876.644-.952 0-1.708-.448-1.848-1.26h4.2c.028-.168.028-.336.028-.448zm-4.228-.364c.168-.7.7-1.148 1.4-1.148.672 0 1.148.42 1.148 1.148h-2.548zm-5.152 2.38c-.42 0-.84-.168-1.12-.448V6.93c.28-.336.7-.504 1.12-.504 1.008 0 1.624.756 1.624 1.904s-.616 1.876-1.624 1.876zm-.392-5.264c-.504 0-.98.14-1.344.42l-.112-.308H15.6v8.54l1.54-.336V11.37c.392.252.868.392 1.4.392 1.68 0 2.996-1.288 2.996-3.108 0-1.876-1.288-3.108-2.996-3.108-.028-.028-.028-.028-.028 0z"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Contract Details — status row only, no ID */}
              <div className="bg-white rounded-2xl shadow-soft p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('preview.contractDetails', 'Détails du contrat')}</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('preview.country')}</span>
                    <span className="font-medium text-gray-900 text-sm">{countryData?.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('preview.type')}</span>
                    <span className="font-medium text-gray-900 text-sm">{getContractTypeLabel()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('preview.status')}</span>
                    <Badge.Status status={contract?.document_status || 'preview'} />
                  </div>
                </div>
              </div>

              {/* Need Changes — demoted to small text link */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/contracts/create')}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors underline underline-offset-2"
                >
                  {t('preview.needChanges')} {t('preview.startNewContract')}
                </button>
              </div>

              {/* Support */}
              <div className="rounded-2xl p-5 border border-gray-100" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}12 100%)` : 'linear-gradient(135deg, #f9fafb 0%, #eff6ff 100%)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${primaryColor}15` }}>
                    <svg className="w-4 h-4" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{t('preview.needHelp')}</h4>
                    <p className="text-xs text-gray-500 mt-1">{t('preview.supportEmail', 'Réponse par email sous 4h')}</p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1 text-sm font-medium mt-2"
                      style={{ color: primaryColor }}
                    >
                      {t('preview.contactSupport')}
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky purchase bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-2xl">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <div className="flex-1">
            <p className="text-xs text-gray-500">{t('preview.mobileBarLabel', 'Bail prêt à télécharger')}</p>
            <p className="text-lg font-bold text-gray-900">{currencySymbol}{price}</p>
          </div>
          <Button
            onClick={handlePurchase}
            loading={purchasing}
            size="lg"
            style={theme ? { background: colors.buttonBg } : { background: 'linear-gradient(135deg, #1d4ed8, #4338ca)' }}
          >
            {purchasing ? t('preview.processing') : `${t('preview.getMyContract', 'Obtenir mon bail')} — ${currencySymbol}${price}`}
          </Button>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        clientSecret={clientSecret}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        price={countryData?.price}
        currency={countryData?.currency}
        productName={getContractTypeLabel()}
        countryName={countryData?.name}
        isGuest={true}
      />
    </div>
  );
};

export default ContractPreview;
